import { apiService } from '../services/api';

const MIN_GRACE_MS = 2 * 60 * 1000;
let cachedGraceMs = 5 * 60 * 1000;
let loadPromise: Promise<number> | null = null;

export function parseDurationToMs(raw: unknown, fallbackMs = 5 * 60 * 1000): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return Math.max(15_000, Math.min(3_600_000, raw * (raw < 1000 ? 1000 : 1)));
  }
  const s = String(raw || '').trim().toLowerCase();
  const m = s.match(/^(\d+)\s*(s|sec|secs|m|min|mins|h|hr|hrs|d)?$/);
  if (!m) return fallbackMs;
  const n = parseInt(m[1], 10);
  const unit = m[2] || 's';
  let ms = n * 1000;
  if (unit.startsWith('m')) ms = n * 60_000;
  else if (unit.startsWith('h')) ms = n * 3_600_000;
  else if (unit === 'd') ms = n * 86_400_000;
  return Math.max(15_000, Math.min(3_600_000, ms));
}

export function onlineGraceMsFromHeartbeat(raw: unknown): number {
  const hb = parseDurationToMs(raw);
  return Math.max(MIN_GRACE_MS, hb * 2);
}

export function getOnlineGraceMs(): number {
  return cachedGraceMs;
}

export function isLastSeenOnline(lastSeen?: string | null, now = Date.now()): boolean {
  if (!lastSeen) return false;
  const t = new Date(lastSeen).getTime();
  if (Number.isNaN(t)) return false;
  return now - t < cachedGraceMs;
}

function extractGrace(payload: any): number {
  const data = payload?.data ?? payload ?? {};
  if (typeof data.online_grace_seconds === 'number' && data.online_grace_seconds > 0) {
    return Math.max(MIN_GRACE_MS, data.online_grace_seconds * 1000);
  }
  if (typeof data.heartbeat_interval_seconds === 'number' && data.heartbeat_interval_seconds > 0) {
    return Math.max(MIN_GRACE_MS, data.heartbeat_interval_seconds * 2000);
  }
  const settings = data.settings || data;
  return onlineGraceMsFromHeartbeat(settings?.heartbeat_interval || data.heartbeat_interval || '5m');
}

export async function loadOnlineGrace(): Promise<number> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const resp = await apiService.getCollectionSettings();
      cachedGraceMs = extractGrace(resp);
    } catch {
      /* keep previous / default */
    }
    return cachedGraceMs;
  })();
  return loadPromise;
}
